import React from 'react';

import { Text } from '../../core-ui';
import { TableView, TableViewProps } from '../../core-ui/TableView';
import formatCurrency from '../../core/support/formatCurrency';
import formatDate from '../../core/support/formatDate';
import { StatusBadge } from '../Vendors/VendorStatus';
import { useNavPersistedState } from '../Navigation';
import { GetVendors_season_vendors as Vendor } from '../../queries/__generated__/GetVendors';
import { OptionsMap } from '../../support/helpers/CategoryOptions';

type Props = Omit<TableViewProps<Vendor>, 'data' | 'columns'> & {
  currency: string;
  optionsMap: OptionsMap;
  vendors: Array<Vendor>;
};

export function BillingVendorTable(props: Props) {
  const { currency, optionsMap, vendors, ...otherProps } = props;

  const [sortBy, setSortBy] = useNavPersistedState<[number, boolean]>(
    'billing-vendor-table-sort-state',
    // Initially sort by column 3 (signup date) descending
    [3, true],
  );

  return (
    <TableView
      data={vendors}
      columns={[
        {
          title: t('Name'),
          render: (vendor) => vendor.name,
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
        },
        {
          title: t('Contact'),
          render: (vendor) => vendor.contact,
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.contact.localeCompare(b.contact) },
        },
        {
          title: t('Category'),
          // TODO: Ensure this works with IDs
          render: (vendor) =>
            vendor.category
              .map((id) => optionsMap.get(id)?.name ?? '')
              .join(', '),
        },
        {
          title: t('Signup Date'),
          render: (vendor) => formatDate(vendor.createdAt),
          tdProps: { whiteSpace: 'nowrap' },
          sortable: {
            sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
          },
        },
        {
          title: t('Status'),
          render: (vendor) => <StatusBadge status={vendor.status} />,
          sortable: { sortFn: (a, b) => a.status.localeCompare(b.status) },
        },
        {
          title: t('Balance'),
          thAlign: 'end',
          render: (vendor) => {
            const balance = Math.round(vendor.balance * 100);
            return (
              <Text
                textAlign="right"
                color={balance > 0 ? 'red.500' : 'green.500'}
              >
                {formatCurrency(balance, currency)}
              </Text>
            );
          },
          sortable: {
            sortFn: (a, b) => a.balance - b.balance,
          },
        },
      ]}
      sortBy={sortBy}
      onSortChange={setSortBy}
      {...otherProps}
    />
  );
}
